import Separator from "@/common/components/separator";
import useHttpProvider from "@/common/hooks/use-http";
import { GiveawayType, Promotion } from "@/features/giveaways/models/giveaway";
import * as GiveawaysService from "@/features/giveaways/services/giveaways";
import { GiveawayDetailsRoute } from "@/features/home/route";
import {
	AccumulatedEntries,
	getUserAccumulatedEntries,
} from "@/features/payments/services/entries";
import * as SubscriptionServices from "@/features/payments/services/subscriptions";
import { ProfileManageMembershipRoute } from "@/features/profile/route";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function CurrentDraws() {
	const navigate = useNavigate();
	const { http } = useHttpProvider();
	const [currentPromotions, setCurrentPromotions] = useState<Promotion[]>([]);
	const [userDrawsWithEntries, setUserDrawsWithEntries] = useState<Promotion[]>(
		[]
	);
	const [hasActiveSubscription, setHasActiveSubscription] =
		useState<boolean>(false);
	const [accumulatedEntries, setAccumulatedEntries] =
		useState<AccumulatedEntries | null>(null);

	const getCurrentPromotions = async () => {
		try {
			// Get all active draws
			const allActiveDraws = await GiveawaysService.getActivePromotions(http);
			setCurrentPromotions(allActiveDraws);

			// Get user's draws with entry counts
			const userDraws = await GiveawaysService.getUserDrawsWithEntryCount(http);
			setUserDrawsWithEntries(userDraws);

			// Check subscription status
			const subscriptionData =
				await SubscriptionServices.getUserActiveSubscription(http);
			setHasActiveSubscription(
				!!(subscriptionData && subscriptionData.subscription)
			);

			// Get accumulated entries
			const accumulated = await getUserAccumulatedEntries(http);
			setAccumulatedEntries(accumulated);
		} catch (error) {
			// Fallback to just active draws if other calls fail
			const response = await GiveawaysService.getActivePromotions(http);
			setCurrentPromotions(response);
		}
	};

	useEffect(() => {
		getCurrentPromotions();
	}, []);

	// Helper function to calculate total entries for a specific draw
	const getTotalEntriesForDraw = (promotion: Promotion) => {
		// Find the user's purchased entries for this specific draw
		const userDraw = userDrawsWithEntries.find((ud) => ud.id === promotion.id);
		const purchasedEntries = userDraw?.totalEntries || 0;

		// Add accumulated entries based on draw type
		const accumulatedForThisDraw =
			promotion.type === GiveawayType.monthly
				? accumulatedEntries?.monthlyPoints || 0
				: accumulatedEntries?.weeklyPoints || 0;

		return purchasedEntries + accumulatedForThisDraw;
	};

	// Helper function to render entries cell
	const renderEntriesCell = (promotion: Promotion) => {
		const isWeekly = promotion.type === GiveawayType.weekly;
		const totalEntries = getTotalEntriesForDraw(promotion);

		// Show subscribe prompt only if weekly, no active subscription, and truly zero accumulated + purchased entries
		if (isWeekly && !hasActiveSubscription && totalEntries === 0) {
			return (
				<span className="web-body-b1">
					0 entries -{" "}
					<span
						className="cursor-pointer text-blue-700 underline"
						onClick={() => navigate(ProfileManageMembershipRoute)}
					>
						subscribe to unlock
					</span>
				</span>
			);
		}

		return <span className="web-body-b1">{totalEntries} entries</span>;
	};

	const baseURL = import.meta.env.VITE_SERVER_API_URL;

	return (
		<div className="flex flex-col gap-8">
			<p className="web-heading-h4">Current Draws</p>

			<table className="w-full table-fixed">
				<thead>
					<tr>
						<th className="web-body-b1 text-black-gray-500 w-4/10 text-left">
							Product
						</th>
						<th className="web-body-b1 text-black-gray-500 w-2/10 text-left">
							Time Left
						</th>
						<th className="web-body-b1 text-black-gray-500 w-3/10 text-left">
							Your Entries
						</th>
						<th className="web-body-b1 text-black-gray-500 w-1/10 text-right">
							Actions
						</th>
					</tr>
				</thead>

				<tbody>
					{currentPromotions.map((promotion, index) => {
						const prizeImage = `${baseURL}/common/image/${promotion.prize.images[0]?.url}`;
						return (
							<>
								{index === 0 && (
									<tr>
										<td colSpan={4} className="h-6"></td>{" "}
									</tr>
								)}
								<tr key={promotion.id}>
									<td className="w-4/10">
										<div className="flex items-center gap-4">
											<img
												src={prizeImage}
												className="h-25 w-25 rounded-[24px] bg-white"
												alt=""
											/>
											<p className="web-body-b2-medium">
												{promotion.prize.title}
											</p>
										</div>
									</td>
									<td className="w-2/10">
										<p className="web-body-b1 text-gray-700">
											{promotion.endDate.toLocaleString()}
										</p>
									</td>
									<td className="w-3/10">{renderEntriesCell(promotion)}</td>
									<td
										onClick={() =>
											navigate(GiveawayDetailsRoute + "?id=" + promotion.id)
										}
										className="web-button w-1/10 cursor-pointer text-right text-blue-700"
									>
										Details
									</td>
								</tr>
								{index !== currentPromotions.length - 1 && (
									<tr>
										<td colSpan={4} className="h-12">
											<Separator />
										</td>
									</tr>
								)}
							</>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export default CurrentDraws;
